export const DISPLAY_NAME_MAX_LENGTH = 120;
/** Max length per field in profile name modal (first / last). */
export const NAME_PART_MAX_LENGTH = 60;

/** Profile fields used when seeding the name editor (DB columns preferred). */
export type NameProfileSlice = {
  first_name?: string | null;
  last_name?: string | null;
};

/**
 * Last whitespace-separated token = last name; all preceding tokens = first name (joined with spaces).
 * Single token → first name only.
 */
export function splitFullNameLastWordIsLastName(full: string): { first: string; last: string } {
  const t = full.trim();
  if (!t) return { first: '', last: '' };
  const parts = t.split(/\s+/).filter(Boolean);
  if (parts.length === 1) return { first: parts[0], last: '' };
  return { first: parts.slice(0, -1).join(' '), last: parts[parts.length - 1] };
}

/** Full display name from `first_name` + `last_name` only. */
export function displayNameFromProfile(profile: NameProfileSlice | null | undefined): string {
  const first = profile?.first_name != null ? String(profile.first_name).trim() : '';
  const last = profile?.last_name != null ? String(profile.last_name).trim() : '';
  return [first, last].filter(Boolean).join(' ');
}

/**
 * Nav / chrome label: structured profile name, then auth metadata full name, then email local part.
 */
export function accountNavDisplayName(
  profile: NameProfileSlice | null | undefined,
  user: { user_metadata?: Record<string, unknown>; email?: string | null } | null | undefined,
  fallbackLabel = 'User',
): string {
  const fromProfile = displayNameFromProfile(profile);
  if (fromProfile) return fromProfile;
  const md = user?.user_metadata;
  const full = typeof md?.full_name === 'string' ? md.full_name.trim() : '';
  if (full) return full;
  const n = typeof md?.name === 'string' ? md.name.trim() : '';
  if (n) return n;
  const emailLocal = user?.email?.split('@')[0]?.trim();
  if (emailLocal) return emailLocal;
  return fallbackLabel;
}

/** First name for greetings; same precedence as the profile editor (`getEditableNameParts`). */
export function greetingFirstName(
  profile: NameProfileSlice | null | undefined,
  user: { user_metadata?: Record<string, unknown> } | null | undefined,
): string {
  return getEditableNameParts(profile, user?.user_metadata).first;
}

/**
 * Seed first/last: prefer `users.first_name` / `users.last_name`, then Auth metadata, then split `name`.
 */
export function getEditableNameParts(
  profile: NameProfileSlice | null | undefined,
  userMetadata: Record<string, unknown> | undefined,
  fallbackFullName?: string,
): { first: string; last: string } {
  const pf = profile?.first_name != null ? String(profile.first_name).trim() : '';
  const pl = profile?.last_name != null ? String(profile.last_name).trim() : '';
  if (pf || pl) return { first: pf, last: pl };

  const fn = typeof userMetadata?.first_name === 'string' ? userMetadata.first_name.trim() : '';
  const ln = typeof userMetadata?.last_name === 'string' ? userMetadata.last_name.trim() : '';
  if (fn || ln) return { first: fn, last: ln };

  const metaFull = typeof userMetadata?.full_name === 'string' ? userMetadata.full_name.trim() : '';
  const metaSingle = typeof userMetadata?.name === 'string' ? userMetadata.name.trim() : '';
  const full = (fallbackFullName?.trim() || metaFull || metaSingle || '') as string;
  return splitFullNameLastWordIsLastName(full);
}

/**
 * Trainer initials for the Library author chip (handoff: trainer-library-exercises-redesign §5.2).
 * Two-letter uppercase; ties to the same name source as the profile editor.
 * Falls back to 'YOU' when nothing resolves so the chip never renders empty.
 */
export function trainerInitials(
  profile: NameProfileSlice | null | undefined,
  user: { user_metadata?: Record<string, unknown> } | null | undefined,
): string {
  const { first, last } = getEditableNameParts(profile, user?.user_metadata);
  if (first && last) return (first[0] + last[0]).toUpperCase();
  if (first && first.length >= 2) return first.slice(0, 2).toUpperCase();
  if (last && last.length >= 2) return last.slice(0, 2).toUpperCase();
  if (first) return first[0].toUpperCase();
  if (last) return last[0].toUpperCase();
  return 'YOU';
}

export function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    const a = parts[0][0];
    const b = parts[parts.length - 1][0];
    return `${a}${b}`.toUpperCase();
  }
  if (parts.length === 1 && parts[0].length >= 2) return parts[0].slice(0, 2).toUpperCase();
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return '?';
}

/** Full local calendar date of signup, e.g. "28 Jan 2026". */
export function formatMemberSince(iso: string | undefined): string | null {
  if (!iso) return null;
  try {
    return new Intl.DateTimeFormat(undefined, {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    }).format(new Date(iso));
  } catch {
    return null;
  }
}

export function roleLabel(role: string | undefined): string {
  if (role === 'trainer') return 'Trainer';
  return 'Member';
}
